import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { H2, OL, P, Pull, UL } from './ui';

const post: Post = { meta: postMeta('online-beat-battles-complete-guide'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        An online beat battle is a real-time or asynchronous competition where music producers
        upload original beats and the audience or a judge panel picks a winner. They have been
        around since the late 1990s, but the format went through a rebirth in the 2020s when
        platforms started running them in the browser with shared sample kits and live blind voting.
      </P>
      <P>
        This is the complete guide: how they work, what platforms exist, how judging happens, what
        the prizes look like, and how to get into your first battle today.
      </P>

      <H2 id="how-it-works">How an online beat battle works</H2>
      <P>Almost every online beat battle follows the same four phases:</P>
      <OL>
        <li>
          <strong>Lobby.</strong> Players join the match. The host or platform locks the genre, BPM,
          and sometimes a sample kit.
        </li>
        <li>
          <strong>Build.</strong> A timer starts. Producers make a beat in their DAW, often using
          the shared kit. Real-time platforms use 10 to 30 minutes; async platforms run 24 hours to
          a week.
        </li>
        <li>
          <strong>Submit.</strong> Each producer uploads their beat. Submissions close automatically
          when the timer ends.
        </li>
        <li>
          <strong>Vote.</strong> Audience or judges score the beats blind (no producer names shown).
          Highest score wins.
        </li>
      </OL>

      <H2 id="formats">Common formats</H2>
      <UL>
        <li>
          <strong>Real-time, shared kit.</strong> Same drums, melodies, and one-shots drop to every
          producer at the same instant. Skill, not gear.
        </li>
        <li>
          <strong>Async with theme.</strong> A theme (sample, BPM, genre, mood) is announced.
          Producers have a week to submit.
        </li>
        <li>
          <strong>Tournament bracket.</strong> Many producers compete over multiple rounds. Win to
          advance. Final round picks a champion.
        </li>
        <li>
          <strong>Speed battle.</strong> 5 or 10 minutes total. Workflow test.
        </li>
        <li>
          <strong>Sample flip.</strong> Everyone gets the same source sample (a few bars of a
          track). Goal is to produce something new from it.
        </li>
      </UL>

      <H2 id="genres">Genres covered</H2>
      <P>
        On Prod Battle the active genres include trap, drill, hip-hop, lo-fi, phonk, house, tech
        house, drum and bass, future bass, dubstep, jersey club, and growing. Each has its own
        leaderboard and ranked tier. New genres are proposed by the community and voted in - that is
        how phonk and jersey club got added.
      </P>

      <H2 id="judging">Judging and scoring</H2>
      <P>
        Most platforms use blind audience voting on a 1 to 5 scale per submission. Producer
        identities stay hidden until results. Some platforms also run weekly judge panels where
        established producers score entries. Both methods produce roughly similar rankings; audience
        voting is faster and more democratic.
      </P>
      <P>
        Ranked play uses an ELO-style rating: you gain points for beating higher-rated opponents and
        lose more for losing to lower-rated opponents. After 10 matches your rating stabilizes and
        you get a tier (bronze, silver, gold, platinum, diamond, masters).
      </P>

      <H2 id="prizes">Prizes</H2>
      <P>Most online battles are not pay-to-play. Prizes vary by platform:</P>
      <UL>
        <li>
          <strong>Reputation</strong>: leaderboard ranking, public profile visibility, badges.
        </li>
        <li>
          <strong>Curation</strong>: top tracks featured on monthly playlists, in newsletters, on
          social channels.
        </li>
        <li>
          <strong>Cash</strong>: weekly tournaments on some platforms run modest prize pools ($50 to
          $500 typically). Larger named tournaments (Battle of the Beat Makers) have hit $10,000.
        </li>
        <li>
          <strong>Sample packs and gear</strong>: sponsor prizes from sample-pack labels and plugin
          companies.
        </li>
      </UL>

      <H2 id="platforms">Platforms hosting online beat battles in 2026</H2>
      <UL>
        <li>
          <strong>Prod Battle</strong>: real-time, multi-genre, shared sample kits, ranked per
          genre, free.
        </li>
        <li>
          <strong>Beat Battle (.net)</strong>: rhythm-game-styled platform, real-time matches.
        </li>
        <li>
          <strong>Audeobox</strong>: async beat battles with public profiles and a learn section.
        </li>
        <li>
          <strong>BeatBattle.app</strong>: community-driven async battles.
        </li>
        <li>
          <strong>IllMuzik Beat Battle League</strong>: forum-format, the original online beat
          battle community since 2000.
        </li>
        <li>
          <strong>Battle of the Beat Makers</strong>: live annual tournament with cash prizes.
        </li>
      </UL>

      <H2 id="how-to-enter">How to enter your first battle today</H2>
      <OL>
        <li>
          Sign up free at{' '}
          <Link to="/auth/sign-up" className="underline decoration-primary/50 underline-offset-2">
            Prod Battle
          </Link>
          . Email, Google, or Discord.
        </li>
        <li>
          Pick a genre you are confident in.{' '}
          <Link to="/genres" className="underline decoration-primary/50 underline-offset-2">
            Browse genres here
          </Link>
          .
        </li>
        <li>
          Hit{' '}
          <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
            Play
          </Link>{' '}
          and choose Quickplay. You will be matched into the next available battle within a minute.
        </li>
        <li>Make your beat with the shared kit. Export. Submit. Wait for voting.</li>
        <li>After voting closes, your beat lives on your profile and your rating updates.</li>
      </OL>

      <H2 id="tips">Tips for first-timers</H2>
      <UL>
        <li>Start in quickplay, not ranked. Lose 5 matches before you go ranked.</li>
        <li>Use a DAW you already know. This is not the moment to switch.</li>
        <li>Export at -8 LUFS or louder. Quiet beats lose.</li>
        <li>Front-load the most arresting sound in the first two seconds.</li>
        <li>Submit on time. An okay beat submitted beats a great beat unsubmitted.</li>
      </UL>

      <Pull>
        The fastest way to learn is to play 10 matches in a week. Everything else is theory.
      </Pull>

      <H2 id="bottom-line">Bottom line</H2>
      <P>
        Online beat battles are one of the fastest ways to improve as a producer. The constraint of
        a timer and a shared kit forces you to make decisions, finish work, and ship beats. The
        feedback from blind voting tells you what audiences actually respond to, not what you think
        they should respond to.
      </P>
      <P>
        Pick a platform, pick a genre, and play your first match this week. The longer-form version
        of every other guide on our blog is just elaboration on this single point.
      </P>
    </>
  );
}
